/* ============================================
   Panier tiroir, l'élément <cart-drawer> (Museau & Co, porté du panier Rue
   des Plantes le 22-09-2026)

   Réécrit pour le tiroir maison (snippets/cart-drawer.liquid). Le CONTRAT de
   Dawn est gardé, car d'autres scripts appellent le tiroir :
   - product-form.js (fiche produit, sticky du pilote Immunité qui clique le
     bouton de la fiche, fenêtre quick-add) : getSectionsToRender(),
     setActiveElement(), puis renderContents(réponse de /cart/add) ;
   - quick-add.js : open(), getSectionsToRender().

   Ce qui est propre au tiroir maison :
   - Échap ferme (Kaching Cart ne le faisait pas) ; le voile et « Fermer » aussi ;
   - le focus reste DANS le tiroir ouvert (Tab et Maj+Tab bouclent) et revient
     sur le bouton qui l'a ouvert à la fermeture ;
   - quantités, retrait, code, suggestions : assets/mc-panier.js.
   ============================================ */

class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.surClavier = this.surClavier.bind(this);
    this.surFocus = this.surFocus.bind(this);

    this.addEventListener('click', (e) => {
      if (e.target.closest('[data-mc-fermer]')) this.close();
    });

    // Clic sur la page hors du tiroir (le voile) : ferme, mais seulement si
    // l'appui a commencé ET fini sur le voile. Un glisser parti du tiroir
    // (sélection de texte, slider) et lâché dehors ne ferme rien.
    // On ferme sur le CLIC (et non au relâché) : au doigt, fermer au relâché
    // laissait le clic qui suit traverser jusqu'à la page dessous (relevé au
    // banc à 390 : le focus tombait dans le champ quantité de la fiche).
    // Un clic dont l'appui a commencé ailleurs n'a pas le voile pour cible.
    this.appuiSurVoile = false;
    this.addEventListener('pointerdown', (e) => {
      this.appuiSurVoile = !!(e.target.closest && e.target.closest('[data-mc-voile]'));
    });
    this.addEventListener('click', (e) => {
      const surVoile = this.appuiSurVoile && !!(e.target.closest && e.target.closest('[data-mc-voile]'));
      this.appuiSurVoile = false;
      if (!surVoile || !this.classList.contains('active')) return;
      e.preventDefault();
      const bouton = document.querySelector('#cart-icon-bubble');
      if (bouton) this.setActiveElement(bouton);
      this.close();
    });
    this.setHeaderCartIconAccessibility();
  }

  get panneau() {
    return this.querySelector('[data-mc-panneau]');
  }

  setHeaderCartIconAccessibility() {
    const lien = document.querySelector('#cart-icon-bubble');
    if (!lien || lien.dataset.mcTiroir) return;
    lien.dataset.mcTiroir = '1';
    lien.setAttribute('role', 'button');
    lien.setAttribute('aria-haspopup', 'dialog');
    lien.addEventListener('click', (e) => {
      e.preventDefault();
      this.open(lien);
    });
    lien.addEventListener('keydown', (e) => {
      if (e.code && e.code.toUpperCase() === 'SPACE') {
        e.preventDefault();
        this.open(lien);
      }
    });
  }

  open(declencheur) {
    if (declencheur) this.setActiveElement(declencheur);
    if (this.classList.contains('active')) {
      this.focusPremier();
      return;
    }
    this.panneau.removeAttribute('inert');
    // Une frame d'écart : sans elle, la transition d'entrée ne joue pas
    // toujours (même remarque que dans le Dawn d'origine).
    requestAnimationFrame(() => {
      this.classList.add('active');
      document.body.classList.add('overflow-hidden');
      this.focusPremier();
    });
    document.addEventListener('keydown', this.surClavier, true);
    document.addEventListener('focusin', this.surFocus);
    if (window.McPanier) {
      window.McPanier.chargerSuggestions(this);
      requestAnimationFrame(() => window.McPanier.calerPaliers(this));
      // Une lecture de /cart.js à l'ouverture : un code ou un article venu
      // d'ailleurs (lien /discount/CODE, autre onglet, app) se voit, et le
      // rendu est redemandé si le total a bougé (RELECTURE-3.md, R2).
      window.McPanier.synchroniser();
    }
  }

  close() {
    if (!this.classList.contains('active')) return;
    this.classList.remove('active');
    this.panneau.setAttribute('inert', '');
    document.body.classList.remove('overflow-hidden');
    document.removeEventListener('keydown', this.surClavier, true);
    document.removeEventListener('focusin', this.surFocus);
    const retour = this.activeElement;
    this.activeElement = null;
    if (retour && document.contains(retour) && typeof retour.focus === 'function') {
      retour.focus({ preventScroll: true });
    }
  }

  focusables() {
    return [
      ...this.panneau.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea, summary, [tabindex]:not([tabindex="-1"])'
      ),
    ].filter((el) => el.getClientRects().length > 0 && !el.closest('[hidden]') && !el.closest('details:not([open]) > :not(summary)'));
  }

  focusPremier() {
    const fermer = this.panneau.querySelector('[data-mc-fermer]');
    (fermer || this.panneau).focus({ preventScroll: true });
  }

  surClavier(e) {
    if (!this.classList.contains('active')) return;
    if (e.key === 'Escape') {
      // Une liste déroulante ouverte se ferme d'abord toute seule.
      e.preventDefault();
      this.close();
      return;
    }
    if (e.key !== 'Tab') return;
    const liste = this.focusables();
    if (!liste.length) {
      e.preventDefault();
      this.panneau.focus();
      return;
    }
    const premier = liste[0];
    const dernier = liste[liste.length - 1];
    const actif = document.activeElement;
    const dedans = this.panneau.contains(actif);
    if (e.shiftKey && (actif === premier || !dedans || actif === this.panneau)) {
      e.preventDefault();
      dernier.focus();
    } else if (!e.shiftKey && (actif === dernier || !dedans)) {
      e.preventDefault();
      premier.focus();
    }
  }

  // Si le focus s'échappe malgré tout (clic dans une iframe d'app, script
  // tiers), on le ramène dans le tiroir.
  surFocus(e) {
    if (!this.classList.contains('active')) return;
    if (this.panneau.contains(e.target)) return;
    this.focusPremier();
  }

  // Réponse de /cart/add (product-form) : les sections
  // demandées par getSectionsToRender() sont dedans.
  renderContents(reponse) {
    const sections = (reponse && reponse.sections) || {};
    // Affiché tout de suite, puis recalé sur le serveur dans la file du panier
    // (couvre aussi la page panier, absente des sections demandées par
    // product-form).
    if (window.McPanier) {
      window.McPanier.appliquerExterne(sections);
    } else {
      this.remplacer(sections['cart-drawer']);
    }
    setTimeout(() => this.open());
  }

  remplacer(html) {
    if (!html) return;
    const source = new DOMParser().parseFromString(html, 'text/html').querySelector('#CartDrawer');
    const cible = this.querySelector('#CartDrawer');
    if (!source || !cible) return;
    cible.innerHTML = source.innerHTML;
    const vide = !!cible.querySelector('.mc-panier--vide');
    this.classList.toggle('is-empty', vide);
  }

  getSectionsToRender() {
    return [
      { id: 'cart-drawer', selector: '#CartDrawer' },
      { id: 'cart-icon-bubble' },
    ];
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

if (!customElements.get('cart-drawer')) customElements.define('cart-drawer', CartDrawer);
